import React, {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Toaster } from 'react-hot-toast';
import {
  audioPath,
  OPPONENT_ACTION_VOLUME,
  playerFlipCardsSprite,
  SFX_SPRITE_ID,
  tableFlipCardsSprite
} from 'constants/audioConstants';
import { REGISTER_SOCKET, SITOUT, UNSUBCRIBE_TO_GAME_UPDATES } from 'constants/eventConstants';
// LEFT_PANE_WIDTH no longer used — chat/history are floating overlays now
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import { FixedChrome, FixedChromeProvider } from 'contexts/FixedChromeContext';
import GameContext, { GameProvider } from 'contexts/GameContext';
import GlobalContext from 'contexts/GlobalContext';
import { GridModeProvider } from 'contexts/GridModeContext';
import { OrientationOverrideProvider } from 'contexts/OrientationOverrideContext';
import { ScaleContextProvider } from 'contexts/ScaleContext';
import SplashScreenContext from 'contexts/SplashScreenContext';
import { TableActiveProvider, useIsActiveTable } from 'contexts/TableActiveContext';
import { TableGlobalScope } from 'contexts/TableGlobalScope';
import { TableIdProvider } from 'contexts/TableIdContext';
import { useIsTableOnScreen } from 'contexts/TableOnScreenContext';
import TableSettingsContext from 'contexts/TableSettingsContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
// ScaleContext removed — no more transform: scale() layout
import Decimal from 'decimal.js';
import { useFeatureFlag } from 'featureFlags';
import gsap from 'gsap';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useDequeueOnSeated } from 'hooks/useDequeueOnSeated';
import { useFeltHydrationRecovery } from 'hooks/useFeltHydrationRecovery';
import { useGuest } from 'hooks/useGuest';
import { useDeferredImBack } from 'hooks/useImBack';
import { useOrientation } from 'hooks/useOrientation';
import useSound from 'hooks/useSound';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import { useTableUsable } from 'hooks/useTableUsable';
import { useViewportWidth } from 'hooks/useViewportWidth';
import { removeTable } from 'reducers/tables';
import { updateHandRank } from 'reducers/user';
import { clearAcceptedSeatOffer } from 'reducers/waitlist';
import { selectMySpotFor } from 'reducers/waitlist.selectors';
import useGameService from 'services/GameService';
import { clearBuyBackInDeclined, isBuyBackInDeclined } from 'utils/buyBackInDeclined';
import { isFinalRunOutBoard } from 'utils/functions';
import { isReplaying } from 'utils/handRecorder';
import { isReconciledAwayTable, markRecentlyExitedTable } from 'utils/recentlyExitedTables';
import {
  boardStageFromHand,
  canPaintTableCelebration,
  countBoardCards,
  diffSeatAction,
  potChipFlightKey,
  potFlightWinnerSeats,
  potWinnerDeclarationKey,
  resolveActionSfx,
  resolveChipCue,
  seatActionsSignature,
  shouldPlayTableSfx
} from 'utils/tableSfx';
import { clampTablesInView, TILED_TABLE_VIEW_ENABLED } from 'utils/tableViewWidth';
import {
  computeActiveTournamentRedirect,
  computeActiveTournamentRedirectFromAssignment,
  computeActiveTournamentRedirectFromSeat,
  shouldBlockGuestFromTournamentTable
} from 'utils/tournamentNavigation';

// getTransformScale removed — no more transform: scale() layout
import Chat from 'components/Chat/Chat';
// GameCommsMenu consolidated into taskbar
import Confetti from 'components/Confetti/Confetti';
import FreshDeskLink from 'components/FreshDeskLink';
import GamePromoProgressIndicator from 'components/PlayerPromos/GamePromoProgressIndicator';
import { isFreeAtTable } from 'components/Streaks/Table/momentPlan';
import StreakTableMoment from 'components/Streaks/Table/StreakTableMoment';
import RebindingTableView from 'components/RebindingTableView/RebindingTableView';
import { isTableSwitching } from 'components/RebindingTableView/tableSwitchTransition';
import { useActiveTables } from 'components/SaloonLobby/useActiveTables';
import SitGoFinishedOverlay from 'components/SitGoResult/SitGoFinishedOverlay';
import SitGoRematchRegistration from 'components/SitGoResult/SitGoRematchRegistration';
import SitGoResultModal from 'components/SitGoResult/SitGoResultModal';
import Table from 'components/Table';
import TournamentDetails from 'components/Table/TournamentDetails';
import TableHeader from 'components/TableHeader';
import { applyTickerShift } from 'components/TableHeader/tickerShift';
import { getTickerVisible } from 'components/TickerBanner/tickerVisibility';
import { HandHistoryPanel } from 'components/HandHistory';
import UserActions from 'components/UserActions';
import AddChipsModal from 'components/UserActions/AddChipsModal';
import AddFundsModal from 'components/UserActions/AddFundsModal';
import AddOnChipsModal from 'components/UserActions/AddOnChipsModal';
import AutoFoldMessageModal from 'components/UserActions/AutoFoldMessageModal';
import BuyInModal from 'components/UserActions/BuyInModal';
import {
  reconcileVote,
  voteFromSeatAction
} from 'components/UserActions/MultiRunActions/voteFromAction';
import PrivateTablePasswordModal from 'components/UserActions/PrivateTablePasswordModal';
import RebuyChipsModal from 'components/UserActions/RebuyChipsModal';
import SeatJoinErrorModal from 'components/UserActions/SeatJoinErrorModal';
import SitOutOverlay from 'components/UserActions/SitOutOverlay';
import TableActions from 'components/UserActions/TableActions';
import TableLoadErrorModal from 'components/UserActions/TableLoadErrorModal';
import TournamentScreenModal from 'components/UserActions/TournamentScreenModal';
import WaitForBuyInModal from 'components/UserActions/WaitForBuyInModal';
import { resolveAutoBuyInOffer } from 'components/Waitlist/autoBuyInOffer';

import { gridCellStyle, gridFeltStyle, gridFooterStyle } from './gameGridLayout';
import { footerRow, pageShell, tableRow } from './gamePageLayout';

import 'App.scss';

/** Module-level flag: splash only shows on first table open per session */
let _splashShown = false;

/**
 * Wrapper that provides TableIdContext before any useTableSelector calls.
 *
 * The per-table providers (GameProvider + ScaleContextProvider) and GameInner
 * are keyed by the DISPLAYED gameID so the whole per-table subtree remounts —
 * and its transient state resets — whenever the shown table changes, exactly as
 * the old panel-level remount did. What's new is that RebindingTableView sits
 * ABOVE them and stays mounted across the change, cross-fading between the old
 * and new table instead of blanking the panel (a close-and-reopen). The
 * displayed gameID lags `activeTableInfo.gameID` during the fade, so the
 * providers still see one stable table at a time.
 */
/**
 * Forces portrait on a felt that is sharing the viewport with others.
 *
 * A no-op at one table in view, where the real viewport orientation IS the
 * right answer — hence a wrapper rather than an override that is always on.
 */
const TileOrientation = ({ tiled, children }: PropsWithChildren<{ tiled: boolean }>) =>
  tiled ? (
    <OrientationOverrideProvider orientation='portrait'>{children}</OrientationOverrideProvider>
  ) : (
    <>{children}</>
  );

const Game = () => {
  const { activeTableInfo, tablesInView, tableWindowStart, setTableWindowStart } =
    useContext(ViewManagerContext);
  const { activeTables } = useActiveTables();
  const viewportWidth = useViewportWidth();
  const gameID = activeTableInfo?.gameID || '';
  const order = activeTables.map((t) => t.gameID);
  // How many felts share the viewport right now, clamped to what the window and
  // the open-table count actually allow.
  const perView = TILED_TABLE_VIEW_ENABLED
    ? clampTablesInView(tablesInView, viewportWidth, order.length)
    : 1;
  // The tables still open, so the carousel can drop kept-alive panels for tables
  // the player has closed. Selected as a joined key (not the object) so this only
  // re-renders when the SET changes, never on a table's 1/s broadcast.
  const openGameIDsKey = useAppSelector((state) =>
    Object.keys((state as any).tables ?? {}).join(',')
  );
  const openGameIDs = useMemo(() => openGameIDsKey.split(',').filter(Boolean), [openGameIDsKey]);
  // Stationary host for the top chrome. It sits OUTSIDE the carousel, so the
  // header controls stay put while the table surface slides beneath them (see
  // contexts/FixedChromeContext). Only the active table's panel fills it.
  const [chromeHost, setChromeHost] = useState<HTMLDivElement | null>(null);
  // Stable render callback: the carousel memoizes each panel's subtree on this
  // function's identity (see RebindingTableView's PanelContent), so its own
  // gesture bookkeeping (mounting neighbours, keep-alive promotion, settle
  // collapse) stops re-rendering every mounted felt. The identity changes on
  // exactly the inputs a panel's output depends on - which table is active,
  // how many share the viewport, and the chrome host.
  const renderPanel = useCallback(
    (displayedGameID: string) => (
      <GameProvider key={displayedGameID} handHistoryTableId={displayedGameID}>
        <ScaleContextProvider>
          <TableIdProvider gameID={displayedGameID}>
            {/* Panels other than the one the player is on (the outgoing one
                mid-slide, and the keep-alive panels RebindingTableView holds
                so switching back costs no remount) are inert: no engine
                emits, no sound, no animation. See TableActiveContext. */}
            <TableActiveProvider value={displayedGameID === gameID}>
              {/* The buy-in family lives in the app-level GlobalContext — one
                  boolean for every open table — so an open buy-in both showed
                  on the next table AND blocked it from opening its own. Each
                  panel gets its own copy, the way every grid cell does. See
                  contexts/TableGlobalScope. */}
              <TableGlobalScope>
                {/* The shared nav — hamburger, support, the table strip, the
                    count control — is ONE thing however many felts are on
                    screen, so the host stays put and only the focused table
                    supplies it. Nulling this host (an earlier pass did) renders
                    the whole nav once per panel, which is what made a
                    four-table layout look like four separate pages. */}
                <FixedChromeProvider
                  value={{ host: chromeHost, isActive: displayedGameID === gameID }}
                >
                  {/* TILE MODE. Two or more felts sharing the width means each
                      is a tall narrow box — portrait-shaped — while the WINDOW
                      is still landscape. Everything on the felt branches on
                      useOrientation, which reads the window, so without this
                      override each table lays itself out wide and is then
                      crushed into a narrow column: seats around an oval that
                      isn't there, and TableActions rendering its icon-only
                      landscape variant instead of the real labelled controls.
                      One provider fixes both, and it is the same one the grid
                      has always used. */}
                  <TileOrientation tiled={perView > 1}>
                    <GridModeProvider value={perView > 1}>
                      <GameInner gridMode={perView > 1} suppliesSharedHeader />
                    </GridModeProvider>
                  </TileOrientation>
                </FixedChromeProvider>
              </TableGlobalScope>
            </TableActiveProvider>
          </TableIdProvider>
        </ScaleContextProvider>
      </GameProvider>
    ),
    [gameID, perView, chromeHost]
  );
  return (
    <div style={tableStageStyle}>
      <RebindingTableView
        gameID={gameID}
        tables={order}
        tablesInView={perView}
        windowStart={tableWindowStart}
        onWindowStartChange={setTableWindowStart}
        openGameIDs={openGameIDs}
      >
        {renderPanel}
      </RebindingTableView>
      {/* Above the sliding panels, and pass-through: the header's own clusters
          re-enable pointer events, so the felt behind the empty areas of this
          layer stays draggable. */}
      <div ref={setChromeHost} style={fixedChromeHostStyle} />
    </div>
  );
};

type GameInnerProps = {
  gridMode?: boolean;
  /**
   * This table is a tile in the single-table carousel rather than a cell in the
   * old grid, so the FOCUSED one supplies the shared nav (portalled to the
   * stationary host above the track — see FixedChrome). Grid cells leave it
   * false: the grid renders its own header above the row.
   */
  suppliesSharedHeader?: boolean;
};

const GameInner = ({ gridMode = false, suppliesSharedHeader = false }: GameInnerProps) => {
  // Owns any "I'm Back" press held through a dealer button / blind assignment.
  // Lives here rather than in SitOutOverlay because that component unmounts the
  // moment the press is accepted, and one instance per table (including per
  // grid cell) scopes the hold correctly. See hooks/useImBack.ts.
  useDeferredImBack();
  const { activeTableInfo, activeView, openTable, returnToLobby } = useContext(ViewManagerContext);
  // False for a panel that is mounted but NOT the table the player is on (the
  // outgoing one mid-slide, or a keep-alive panel held so switching back costs no
  // remount). Such a panel must stay silent on the wire: the engine has one
  // session per player, so a second panel emitting ONLINE / SubscribeToGameUpdates
  // / SITOUT would cross-pollute it with the wrong tableID. Always true in the
  // grid and in tests (no provider) — see contexts/TableActiveContext.
  const isActiveTable = useIsActiveTable();
  // Whether the player can SEE this felt — supplied by RebindingTableView (a
  // held panel is parked and hidden) and MultiTableGrid (the grid stays mounted
  // behind single-table view). Distinct from `isActiveTable`, which is the
  // focus/ownership gate and is false for visible non-focused grid cells and
  // for the second tile of a tiled layout. See contexts/TableOnScreenContext.
  const onScreen = useIsTableOnScreen();
  // The CONSEQUENTIAL threshold only — graced, because acting on it forces a
  // reconnect and ultimately offers the player a modal that ends in leaving the
  // table. The felt's cosmetic loading chip reads the ungraced `seatsPending`
  // instead; see hooks/useTableUsable for why the two must not share a number.
  const { unusable } = useTableUsable();
  const tableIdFromContext = useTableId();
  // Bind to the gameID from TableIdContext in BOTH modes. In grid it's the
  // cell's gameID (set by MultiTableGrid); in single-table it's the DISPLAYED
  // gameID supplied by Game's RebindingTableView — which lags the URL during a
  // cross-fade swap. Reading it (rather than activeTableInfo.gameID/the URL)
  // keeps this component's logic on the exact table it's rendering while the
  // surface transitions. Falls back to the URL before the provider is set.
  const gameID = tableIdFromContext || activeTableInfo?.gameID;
  const gameTypeFromTable = useTableSelector((table) => table.game?.game);
  // Same idea for game type: prefer the displayed table's own redux state; fall
  // back to the URL gameType only before the first GOT_TEXAS/GOT_OMAHA lands.
  const gameType = gameTypeFromTable || activeTableInfo?.gameType || 'texas';
  const currentTable = useTableSelector((table) => table);
  const game = useTableSelector((table) => table.game);
  const hand = useTableSelector((table) => table.game.hand);
  const streakGameNo = useTableSelector((table) => table.game.gameNo);
  const streakDealerSeat = useTableSelector((table) => table.game.dealer);
  const streakFree = useTableSelector((table) =>
    isFreeAtTable({
      isSeated: table.currentPlayer.isSeated,
      isWaiting: table.currentPlayer.isWaiting,
      isFolded: table.currentPlayer.isFolded,
      hand: table.game.hand
    })
  );
  const tableType = useTableSelector((table) => table.game.tabletype);
  // EN-11109: the Sit & Go result views subscribe to the table list, so they
  // mount only while the flag is on; off leaves the panel as it was.
  const sitGoResultViews = useFeatureFlag('sitGoResultModal') && tableType === 't';
  const lastplayer = useTableSelector((table) => table.game.lastplayer);
  const tournamentId = useTableSelector((table) => table.game.tournamentId);
  const title = useTableSelector((table) => table.game.title);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  // Table stake, for sizing the ante's chip sample (see resolveChipCue).
  const bb = useTableSelector((table) => table.game.bb);
  const ante = useTableSelector((table) => table.game.ante);
  const players = useTableSelector((table) => table.players);
  const { muteOtherPlayers, muteOtherPlayersSetByUser } = useContext(TableSettingsContext);
  // The snapshot's prizePool is the raw collected pool; the Tournament Info
  // fallback must match the status feed's GREATEST(guarantee, prizepool), or a
  // GTD shows its collected amount until the first status row lands.
  const rawPrizePool = useTableSelector((table) => table.game.prizePool);
  const guarantee = useTableSelector((table) => table.game.guarantee);
  const prizePool = Math.max(Number(rawPrizePool) || 0, Number(guarantee) || 0);
  const tStatus = useTableSelector((table) => table.game.status);
  const triggerBuyIn = useTableSelector((table) => table.game.triggerBuyIn);
  const serverTime = useAppSelector((state) => state.serverDetails.serverTime);
  const orientation = useOrientation();
  // Guests observe only — hide the waitlist and promo overlays. The table-action
  // rail stays: SitOut/StandUp self-hide when not seated, leaving just Exit Table
  // so a guest can leave. Seat/join prompts are handled in OpenSeat/JoinButton.
  const guest = useGuest();
  // Landscape single-table view floats the engine-driven table-control cluster
  // (Sit Out / Stand Up / Exit) on a right rail instead of the footer taskbar.
  const showTableActionRail = !gridMode && orientation === 'landscape';
  // Whether the in-table ticker is showing (reported by TableHeader). Shifts
  // the landscape and grid action rails down to clear it. Seeded from the
  // banked value so a remount doesn't replay the shift animation.
  const [tickerRailVisible, setTickerRailVisible] = useState(() => getTickerVisible('table'));
  const elementRef = useRef<HTMLDivElement>(null);
  const buyInTriggerTimer = useTableSelector((table) => table.game.buyInTriggerTimer);

  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  // Once the engine seats me at a table I was queued for, drop me from that
  // waitlist (Unity does this in JoinGame's onSuccess — BuyInView.cs:688).
  useDequeueOnSeated();
  const userBalance = useAppSelector((state) => state.user.playerBankAmount);
  const finalBalance = Decimal.sum(
    new Decimal(userBalance),
    // `?? 0`: a closed table can be wiped to `players: []` (clearTablePlayers)
    // while currentPlayer.isSeated is still true, so players[seatId] is
    // undefined — `new Decimal(undefined)` throws and trips the error boundary.
    new Decimal(isSeated ? players[seatId]?.stack ?? 0 : 0)
  );

  const { joinTable, getTableCards, leaveTable } = useGameService();

  const observeMode = activeTableInfo?.observe || 'no';
  const dispatch = useAppDispatch();

  const { socket, setGameType, reconnect } = useContext(SocketContext);

  // "Spinning of death" recovery — ALL views (single-table + every grid cell;
  // mobile portrait/landscape included, since those are the same GameInner). If a
  // table never hydrates (no gotTexas → empty tabletype) within a jittered window
  // after opening, force ONE reconnect so a fresh connectionId gets the server's
  // initial snapshot. Client-only mitigation for the RCA item-C fan-out race.
  // Safe in grid: the reconnect is global, but the per-gameID one-shot guard + the
  // shared cooldown collapse several stuck cells into a SINGLE reconnect that
  // re-hydrates every open table at once — no per-cell storm, no loop. Runs per
  // GameInner instance (one per grid cell).
  // `enabled` is the ON-SCREEN gate: a kept-alive panel, or the grid sitting
  // mounted behind single-table view, must never force a (global) reconnect on
  // behalf of a table nobody is looking at. It is deliberately the SAME signal
  // TableLoadErrorModal gates its failure clock on, so the one-shot reconnect
  // always gets its chance before the terminal error is raised for a table.
  // It was `isActiveTable`, which in grid/tiled mode is the FOCUSED-cell gate:
  // a stuck non-focused cell was never offered a reconnect, yet (once the error
  // modal shipped) could still be declared failed.
  // `hydrated` is the shared USABILITY predicate, not `!!tableType`. A table
  // whose seat data never arrived reported itself hydrated, so this hook's
  // early `if (hydrated) return` fired and the one-shot reconnect — the only
  // automatic repair for that state — was never attempted. See
  // hooks/useTableUsable.
  useFeltHydrationRecovery(gameID, !unusable, reconnect, onScreen);

  // Set socket game type when table is active
  useEffect(() => {
    if (activeView === 'table' && gameType) {
      setGameType(gameType);
    }
  }, [activeView, gameType, setGameType]);

  const { setCloseBuyInModal, setOpenAddFunds, setBuyinAmount, setAskBuyinTable } =
    useContext(GlobalContext);
  // Sprite-backed: both card assets have dead air at the start (89ms / 138ms),
  // which is what made the reveal cues sound late regardless of trigger timing.
  // See audioConstants.
  const [playTableCardSprite] = useSound(audioPath.tableFlipCards, {
    sprite: tableFlipCardsSprite
  });
  const playTableCard = useCallback(
    () => playTableCardSprite({ id: SFX_SPRITE_ID }),
    [playTableCardSprite]
  );
  const [playChipsToPot] = useSound(audioPath.chipsToPot);
  // OWN vs OPPONENT, for every action cue.
  //
  // The player's own turn is a story with a beginning and an end: it starts
  // (players_turn), it runs down (the countdown), and it ENDS in an action —
  // you called, you folded, you were folded for you. All of it has to reach the
  // player at a table they are not looking at, or multi-tabling means watching
  // one table and guessing at the rest. So the own-action cues set crossTable
  // exactly like the turn and countdown cues do (EN-10577 AC2).
  //
  // Opponents' actions are the opposite: they are ambient table texture, they
  // belong only to the table being watched, and they are the ones that get
  // noisy at four tables. Same SAMPLE deliberately — pairing an action with a
  // sound only trains the player if the pairing is consistent — but quieter, so
  // your own action still reads as yours.
  const [playBetChipsOwn] = useSound(audioPath.betChips, { crossTable: true });
  const [playBetChips] = useSound(audioPath.betChips, { volume: OPPONENT_ACTION_VOLUME });
  // The single-chip sample, for a wager at or under the big blind. The only
  // wager reaching THIS effect is the ante (resolveActionSfx maps no other
  // action to 'bet'; every other bet, call and raise gets its cue from the
  // seat's bet pill in Table/Bet.tsx) — and an ante is a single small forced
  // chip, so it takes the same rule rather than a special case of its own.
  const [playSmallBetOwn] = useSound(audioPath.smallBet, { crossTable: true });
  const [playSmallBet] = useSound(audioPath.smallBet, { volume: OPPONENT_ACTION_VOLUME });
  const { playerMultiRunVote, setPlayerMultiRunVote } = useContext(GameContext);
  const runs = useTableSelector((table) => table.game.runs);
  const {
    showSplash: showSharedSplash,
    hideSplash: hideSharedSplash,
    playIntro,
    cancelIntro
  } = useContext(SplashScreenContext);
  const [isDealingPlayerCard, setIsDealingPlayerCard] = useState(true);
  const [showSplash, setShowSplash] = useState(!_splashShown);
  if (showSplash) _splashShown = true;

  // Wall clock at which this seat was first seen holding a local vote the
  // server had not echoed. Zeroed whenever there is nothing to wait for.
  const unechoedVoteSinceRef = useRef(0);

  // Reconcile this player's own run-it-twice vote against the server (EN-663 D2).
  //
  // `p{seat}action` — surfaced as players[seat].action — is the durable record
  // voteMultiRun writes alongside the tally, and it is the ONLY thing that comes
  // back: the vote emit is answered by neither writer. So it does double duty
  // here. It restores a vote the client lost (a reconnect mid-window used to
  // re-arm the buttons showing no prior choice), and its ABSENCE, once the
  // client has watched for it across a few frames, is what says a vote the
  // client recorded optimistically never landed — at which point the buttons
  // are handed back while the window is still open, the same way PokerActions
  // releases a held action it could not confirm. See reconcileVote.
  //
  // This is ALSO the per-hand reset (EN-663). The vote is state of one window, so
  // a frame whose `runs` no longer carries 'v' clears it — a rule that holds on
  // every frame rather than only on the two the `hand` switch below used to key
  // its reset off. Those (hand ''/1) each last a single processor tick, and a
  // client that missed one carried the last hand's vote into the next window:
  // pre-selected in green, buttons disabled, and real clicks dropped by
  // GameService's send guard until the echo grace expired. Reported against grid
  // view, where a second mounted copy of the table doubles the chances of missing
  // the frame, but nothing about it was grid-specific.
  useEffect(() => {
    const windowOpen = String(runs ?? '').includes('v');
    const echoed = windowOpen ? voteFromSeatAction(players?.[seatId]?.action) : '0';
    const waitingForEcho = windowOpen && echoed === '0' && playerMultiRunVote !== '0';
    if (!waitingForEcho) unechoedVoteSinceRef.current = 0;
    else if (!unechoedVoteSinceRef.current) unechoedVoteSinceRef.current = Date.now();

    const settled = reconcileVote(
      echoed,
      playerMultiRunVote,
      unechoedVoteSinceRef.current ? Date.now() - unechoedVoteSinceRef.current : 0,
      windowOpen
    );
    if (settled !== null) {
      unechoedVoteSinceRef.current = 0;
      setPlayerMultiRunVote(settled);
    }
  }, [runs, playerMultiRunVote, players, seatId, setPlayerMultiRunVote]);

  // Cold-start "grand entrance": once currentTable has actually arrived and
  // this is the first table shown this session, play the branded intro once
  // (via SplashScreenContext.playIntro — self-dismissing, unlike the loading
  // gates above) and clear our own local flag when it reports back.
  useEffect(() => {
    if (!showSplash) return undefined;
    playIntro('gameIntro', () => setShowSplash(false));
    return () => cancelIntro('gameIntro');
  }, [showSplash, playIntro, cancelIntro]);
  const [showConfetti, setShowConfetti] = useState(false);
  // Bumped every time a new pot pays this seat during showdown so the Confetti
  // canvas REMOUNTS with fresh particles. Without a changing key, calling
  // `setShowConfetti(true)` while the previous burst is still on screen is a
  // React no-op — the canvas stays put and the side-pot's celebration is
  // dropped. See the confetti effect below.
  const [confettiKey, setConfettiKey] = useState(0);
  // Showdown progress — drives the pot-award cue (see potChipFlightKey).
  const showdown = useTableSelector((table) => table.game.showdown);
  // Pots keyed by index (numeric-keyed object from the engine, not an array).
  const pots = useTableSelector((table) => table.game.pots);
  // TableHistory/Chat contexts no longer needed here — they're self-contained overlays
  const [playCollectChips] = useSound(audioPath.collectChips);
  const [playCheckOwn] = useSound(audioPath.check, { crossTable: true });
  const [playCheck] = useSound(audioPath.check, { volume: OPPONENT_ACTION_VOLUME });
  const [playFoldOwn] = useSound(audioPath.fold, { crossTable: true });
  const [playFold] = useSound(audioPath.fold, { volume: OPPONENT_ACTION_VOLUME });
  // Showdown hole-card flip cue (Unity parity: UIPlayer plays PlayerFlipCards at
  // the FindWinners hand phase). The asset existed in audioConstants but was
  // never wired up — React was playing the table-card sound at showdown instead.
  const [playPlayerFlipCardsSprite] = useSound(audioPath.playerFlipCards, {
    sprite: playerFlipCardsSprite
  });
  const playPlayerFlipCards = useCallback(
    () => playPlayerFlipCardsSprite({ id: SFX_SPRITE_ID }),
    [playPlayerFlipCardsSprite]
  );
  // Audio-gate inputs are read through refs inside the sound effect below.
  // Putting them in its dependency array instead would make the whole effect —
  // including the `switch (hand)` that fires the table-card cues — re-run when
  // the player toggles the setting or their seat resolves, replaying sounds at
  // moments that never used to make noise. Refs keep the gate reading current
  // values while leaving the effect's re-run cadence exactly as it was.
  const muteOtherPlayersRef = useRef(muteOtherPlayers);
  muteOtherPlayersRef.current = muteOtherPlayers;
  const muteIsExplicitRef = useRef(muteOtherPlayersSetByUser);
  muteIsExplicitRef.current = muteOtherPlayersSetByUser;
  const seatIdRef = useRef(seatId);
  seatIdRef.current = seatId;
  // The table's blind and ante, read at cue time to size the ante's chip
  // sample (resolveChipCue). Refs, not dependencies: a tournament blind level
  // rising must not re-run the action-cue effect and replay a sound.
  const bigBlindRef = useRef(bb);
  bigBlindRef.current = bb;
  const anteRef = useRef(ante);
  anteRef.current = ante;
  // Last action cue actually played, as "seat:cue" — de-dupes repeat runs of the
  // effect so one action can't sound twice as the hand resolves.
  // Previous per-seat action snapshot — the action-cue trigger (see below).
  const prevSeatActionsRef = useRef<string>('');
  // Rendered board stage last update (-1 none, 0 flop, 1 turn, 2 river); null
  // until the first run so a mid-hand join does not cue the existing board.
  const prevBoardStageRef = useRef<number | null>(null);
  // Pending showdown-flip cue, so leaving the table (or a re-entered showdown)
  // cannot fire it against an unmounted view.
  const { setOpenAskBuyin, setIsTriggerBuyIn, isTriggerBuyIn, openAskBuyin } =
    useContext(GlobalContext);
  const tablelow = useTableSelector((table) => table.game.tablelow);
  const [hasTriggered, setHasTriggered] = useState(false);

  const isValidObserveMode = (observeMode: any) => {
    if (observeMode === 'yes' || observeMode === 'no') {
      return true;
    }

    return false;
  };

  // A seat offer claimed from the JOIN chip or the lobby card flags its gameId
  // in redux; the matching table opens its OWN buy-in modal here. This is what
  // makes the buy-in appear in grid view, where each cell has its own
  // GlobalProvider that app-level chrome can't reach.
  const acceptedOfferGameId = useAppSelector((state) => state.waitlist.acceptedOfferGameId);
  useEffect(() => {
    // In single-table view the buy-in modal is app-level chrome: an inert panel
    // popping it would show the wrong table's buy-in over the felt.
    if (!isActiveTable) return;
    if (acceptedOfferGameId !== null && gameID && Number(gameID) === acceptedOfferGameId) {
      setAskBuyinTable({ gameID: String(gameID), game: gameType || '' });
      setOpenAskBuyin(true);
      dispatch(clearAcceptedSeatOffer());
    }
  }, [acceptedOfferGameId, gameID, isActiveTable, setOpenAskBuyin, dispatch]);

  // Waitlist seat is ready on the table the player is currently on — whether
  // they were already here or just opened it from a Your Tables preview — so
  // pop the buy-in automatically. Fires ONCE per offer window (keyed on the
  // accept-start time + bumpCount) so dismissing the buy-in doesn't re-pop it
  // until the seat is re-offered; resets when the player leaves that waitlist.
  const myWaitlistSpot = useAppSelector(selectMySpotFor(Number(gameID)));
  const autoBuyInOfferSigRef = useRef<string | null>(null);
  useEffect(() => {
    const { open, sig } = resolveAutoBuyInOffer(
      myWaitlistSpot,
      isSeated,
      autoBuyInOfferSigRef.current
    );
    autoBuyInOfferSigRef.current = sig;
    // Track the offer window even while inert (so it can't re-pop later), but
    // only the table the player is on may open the modal.
    if (open && isActiveTable) {
      setAskBuyinTable({ gameID: String(gameID), game: gameType || '' });
      setOpenAskBuyin(true);
    }
    // gameID/gameType/setAskBuyinTable are read in the body — same omission and
    // same risk as OpenSeat's (JRam review, #3721).
  }, [
    myWaitlistSpot,
    isSeated,
    isActiveTable,
    gameID,
    gameType,
    setAskBuyinTable,
    setOpenAskBuyin
  ]);

  useEffect(() => {
    // Skip the idle buy-back-in prompt on a table the player already declined
    // (Unity parity — they observe instead of being re-trapped in the rebuy
    // loop). The flag is cleared on a real rebuy or when the engine resets the
    // trigger (triggerBuyIn === -1).
    if (!isActiveTable) return;
    if (
      isSeated &&
      serverTime - triggerBuyIn > buyInTriggerTimer &&
      triggerBuyIn > 0 &&
      !isBuyBackInDeclined(gameID)
    ) {
      setIsTriggerBuyIn(true);
    }
  }, [serverTime]);

  useEffect(() => {
    // leaveTable() below emits on the shared engine session — inert panels never
    // run this branch.
    if (!isActiveTable) return;
    if (
      isSeated &&
      !hasTriggered &&
      !openAskBuyin &&
      serverTime - triggerBuyIn > buyInTriggerTimer &&
      triggerBuyIn > 0 &&
      !isBuyBackInDeclined(gameID)
    ) {
      leaveTable(true);
      setBuyinAmount(tablelow);
      // Bind which table the buy-back belongs to — see GlobalContext.askBuyinTable.
      // `gameType`, not a second selector for the same field: this file had two
      // answers for one value (JRam review, #3721). gameType IS table.game.game
      // with fallbacks, and this branch requires triggerBuyIn > 0 — which comes
      // off the same slice — so the slice is hydrated here and the fallback
      // cannot fire. One source, no behaviour change.
      setAskBuyinTable({ gameID: String(gameID), game: gameType || '' });
      setOpenAskBuyin(true);
      setHasTriggered(true);
    }

    if (triggerBuyIn === -1) {
      setHasTriggered(false);
      setIsTriggerBuyIn(false);
      clearBuyBackInDeclined(gameID);
    }
  }, [isTriggerBuyIn, triggerBuyIn, openAskBuyin]);

  useEffect(() => {
    // Grid cells are display-only mirrors; the single-table view owns join
    // emission. Without this gate, every hidden grid cell fires its own
    // joinTable on URL changes and cross-pollutes the engine session with
    // the wrong tableID/gameType.
    if (gridMode) return;
    // During a hand replay the table is driven entirely by replayed frames — do
    // NOT join/subscribe the live table, or it would keep streaming live hands
    // after the replay ends.
    if (isReplaying()) return;
    // Only the panel the player is on joins. A kept-alive / outgoing panel would
    // otherwise re-join its own table on any dep change (e.g. a socket
    // reconnect) and hand the engine the wrong tableID.
    if (!isActiveTable) return;
    if (activeView === 'table' && gameID && gameType) {
      if (isValidObserveMode(observeMode)) {
        joinTable(gameID, gameType);
      }
    }
  }, [gameID, gameType, activeView, socket, gridMode, isActiveTable]);

  useEffect(() => {
    if (gridMode) return;
    // Skip live socket registration during a replay (see joinTable effect above).
    if (isReplaying()) return;
    // Same rule as joinTable: an inert panel does not register its table.
    if (!isActiveTable) return;
    if (!socket || activeView !== 'table' || !gameID || !gameType) return;

    socket.emit(REGISTER_SOCKET, { page: 'game', gameID: gameID, gameType: gameType });
    socket.emit(REGISTER_SOCKET, { page: 'lobby' });

    if (tournamentId) {
      socket.emit(REGISTER_SOCKET, {
        page: 'tournament',
        tournamentId: tournamentId,
        isRegister: true
      });
    }
  }, [socket, tournamentId, gameID, gameType, activeView, gridMode, isActiveTable]);

  // Active-player tournament restriction.
  //
  // Each GameInner instance owns this for its own table. The /table/:id
  // route mounts a single GameInner; /grid mounts one GameInner per tile via
  // MultiTableGrid — both code paths give us a TableIdContext bound to the
  // specific gameID, so the `leaveTable` we pull from useGameService below
  // is scoped to *this* table and sends the full SITOUT + UNSUBSCRIBE
  // payload (engine-side session teardown + localStorage flag to block
  // App.tsx auto-resub). A ViewManager-level watcher couldn't do that — it
  // would have to hand-construct the payload without TableIdContext-bound
  // state, and the engine ignores partial payloads.
  //
  // computeActiveTournamentRedirect returns the assigned gameID only when
  // (a) tournamentId is set on this table, (b) the player has status 'P' on
  // that tournament, (c) the assigned seat exists and isn't this gameID.
  // For cash tables / busted players / non-registered players it returns
  // null and the effect is a no-op.
  const playerTournamentDetails = useAppSelector((state) => state.lobby.playerTournamentDetails);
  // state.tables is the seat-heuristic fallback source when
  // playerTournamentDetails is empty (e.g., reload-into-table bypasses Lobby).
  const allTablesForWatcher = useAppSelector((state) => (state as any).tables) as Record<
    string,
    any
  >;
  // Fields for the explicit SITOUT / UNSUBSCRIBE payload below. Pulled
  // straight from state.user (not via useGameService's baseParams) because
  // useTableSelector falls back to state.currentTable when state.tables[gid]
  // isn't fully hydrated yet — which means baseParams.gameID can name the
  // player's seated table instead of this cell's gameID, and the engine
  // silently no-ops the unsubscribe.
  const playerGUID = useAppSelector((state) => state.user.playerGUID || '');
  const playerName = useAppSelector((state) => state.user.playerName || '');
  const playerDisplayName = useAppSelector((state) => state.user.displayName || '');
  const playerLevel = useAppSelector((state) => state.user.playerLevel || 0);
  useEffect(() => {
    if (!gameID || !gameType) return;
    // Never let an inert panel tear a table down or drive navigation: it emits
    // SITOUT + UNSUBSCRIBE and can call openTable. Only the table the player is
    // on runs this.
    if (!isActiveTable) return;
    // The player is deliberately OBSERVING this table (?observe=yes) — e.g. they
    // busted and chose "Observe Game". Never run the wrong-table redirect for an
    // observer: it would recompute a "should-be" table from playerTournamentDetails
    // and yank them off the table they explicitly chose to watch (reported bug).
    if (observeMode === 'yes') return;
    // Cleanup only runs when the user lands on a /table/:id URL — the wrong
    // table they typed into the address bar or clicked from a stale link.
    // In /grid view (MultiTableGrid mounts one GameInner per cell), every
    // visible cell is a table the user explicitly opened; auto-cleanup
    // there caused gotTexas/gotOmaha race conditions to remove tables the
    // player had just joined (omaha cash regression seen on refresh).
    if (gridMode) return;
    // Hard invariant: never clean up a table the player is currently seated
    // at. Seated means they explicitly joined and presumably bought in;
    // wrong-tournament heuristics are for OBSERVED tables only. Without this
    // guard, edge cases where two seated tables briefly appear to share a
    // tournamentId (mid-balancing moves, late gotTexas/gotOmaha races, etc.)
    // would yank the player out of a cash/tournament seat they joined on
    // purpose. We read isSeated from the same authoritative source as
    // tournamentId — state.tables[gid] direct, no useTableSelector fallback.
    const thisTableState = allTablesForWatcher?.[String(gameID)];
    if (thisTableState?.currentPlayer?.isSeated === true) return;
    // Read this table's tournamentId DIRECTLY from state.tables, not via
    // the useTableSelector hook used elsewhere in this component. Why:
    // useTableSelector falls back to state.currentTable when state.tables[gid]
    // isn't hydrated yet (initial render before gotTexas arrives). For a cash
    // table opened right after a tournament table, that fallback returns the
    // tournament's tournamentId — and the seat-heuristic redirect then yanks
    // the user away from the cash table they just opened. Reading directly
    // from state.tables[gameID] returns undefined until gotTexas populates
    // it, and 0/undefined for cash tables once it has — both correctly
    // skip the redirect.
    const thisTableTournamentId = thisTableState?.game?.tournamentId;
    // Primary: lobby data (works when playerTournamentDetails is populated).
    // Fallback: seat heuristic (works when lobby data hasn't loaded — e.g.,
    // the user reloaded directly into a table page bypassing /lobby).
    const target =
      computeActiveTournamentRedirect(
        thisTableTournamentId,
        gameID,
        playerTournamentDetails,
        // Pass the live tables so a stale 'P' record can't redirect a busted
        // player onto their old table's dead "joining" seat (see guard).
        allTablesForWatcher
      ) ??
      computeActiveTournamentRedirectFromSeat(thisTableTournamentId, gameID, allTablesForWatcher) ??
      // Dark-table fallback: this table's felt never hydrated so its tournamentId
      // is unknown and the two resolvers above bail. Happens after a closed-table /
      // final-rebalancing move — the engine reseated the player but the client is
      // stranded on the dead table's URL. Follow the player's own assignment
      // (status 'P' + server-raised shouldAutoRedirect) to the new seat.
      (isReconciledAwayTable(gameID)
        ? computeActiveTournamentRedirectFromAssignment(gameID, playerTournamentDetails)
        : null);
    if (target === null) return;
    const gidStr = String(gameID);
    // Mark BEFORE emit — blocks App.tsx auto-resub and drops any in-flight
    // gotTexas / gotOmaha for this gameID for the 30s grace window.
    markRecentlyExitedTable(gidStr);
    if (socket) {
      // Build the payload by hand with the cell's actual gameID so neither
      // socketId/baseParams indirection nor the currentTable fallback in
      // useTableSelector can ship the wrong gameID. Mirrors the field set
      // GameService.leaveTable produces (engine consumes these fields).
      const teardownPayload = {
        socketId: (socket as any).id,
        gameID: gidStr,
        tableID: gidStr,
        game: gameType,
        playerGUID,
        username: playerName,
        displayName: playerDisplayName,
        level: playerLevel,
        seatId: -1,
        clubId: 0,
        language: 'en',
        tournamentId,
        uid: '',
        data: ''
      };
      socket.emit(SITOUT, {
        ...teardownPayload,
        data: '&leaveGame=true&exitFlag=true&immediate=true'
      });
      socket.emit(UNSUBCRIBE_TO_GAME_UPDATES, teardownPayload);
    } else {
      // Surfacing a soft warn here: emit is silently skipped, which means
      // the engine session won't be torn down and the table will linger
      // until App.tsx's GOT_PLAYER_GAMES filter catches it on the next tick.
      console.warn('[WrongTableCleanup] socket is null — emit skipped for', gidStr);
    }
    dispatch(removeTable(gidStr));
    // Redirect only in single-table view. Grid cells don't drive the URL —
    // the cell unmounts naturally when its state.tables slice disappears.
    if (!gridMode && activeView === 'table') {
      // forceSingle: this is a table SWAP (a balancing move landed the player
      // on a new gameID), not a new open — the same convention as the swipe
      // switches in SlideContainer. The removeTable above has not flushed to
      // the state openTable's closure reads, so its slot math would count the
      // outgoing table plus the new one, read 2, and route a single-table
      // player into grid chrome (EN-10557). This is the FALLBACK redirect (the
      // felt never hydrated); the primary balancing redirect lives in
      // TournamentScreenModal and carries the same option.
      openTable(String(target), gameType, 'no', { forceSingle: true });
    }
  }, [
    gridMode,
    isActiveTable,
    observeMode,
    activeView,
    tournamentId,
    gameID,
    gameType,
    playerTournamentDetails,
    allTablesForWatcher,
    playerGUID,
    playerName,
    playerDisplayName,
    playerLevel,
    socket,
    dispatch,
    openTable
  ]);

  // Guests must not observe tournament/SnG tables. The main click-to-observe
  // affordance (TournamentDetails' Tables-tab Observe button) is gated at
  // the source, but this catches every other way a guest could land here —
  // a direct/deep-linked table URL, the header's table switcher, or a nav
  // chip for a table already (somehow) in state.tables. tableType only
  // resolves once the subscribe's GOT_TEXAS/GOT_OMAHA arrives, so a guest
  // can briefly flash onto the felt before this fires and leaves/redirects.
  useEffect(() => {
    if (!gameID || !shouldBlockGuestFromTournamentTable(guest, tableType)) return;
    // leaveTable() emits on the shared engine session — inert panels must not.
    if (!isActiveTable) return;
    leaveTable(true);
    dispatch(removeTable(String(gameID)));
    if (gridMode) return;
    returnToLobby();
  }, [guest, gameID, tableType, gridMode, isActiveTable, leaveTable, dispatch, returnToLobby]);

  useEffect(() => {
    getTableCards();

    // Whether cues attributable to the seat that acted last (its showdown flip,
    // its chips going to the pot) may sound for this player. Unity gates the
    // same cues per-seat via IsPOVPlayer(); see utils/tableSfx.
    const playsForActingSeat = shouldPlayTableSfx({
      origin: 'player',
      actorSeatId: lastplayer,
      currentPlayerSeatId: seatIdRef.current,
      muteOtherPlayersSounds: muteOtherPlayersRef.current,
      muteOtherPlayersIsExplicit: muteIsExplicitRef.current
    });

    switch (hand) {
      case '':
        if (isSeated && finalBalance.lt(game.minBalanceToPlay) && game.tabletype === 's') {
          setOpenAddFunds(true);
        }
        dispatch(updateHandRank({ name: 'High Card', value: 0 }));
        break;
      case '1':
        //Transfer this to hand == 15?
        dispatch(updateHandRank({ name: 'High Card', value: 0 }));
        setCloseBuyInModal(false);
        break;
      case '4':
        // playSoundDealingCard('dealingCards');
        break;
      case '5':
        // Deal CARDS
        if (isDealingPlayerCard) {
          playTableCard();
          setIsDealingPlayerCard(false);
        }
        break;
      // Flop / turn / river deliberately emit NO cue here — the board cue is
      // data-driven now, in its own effect below. Keying it on the hand phase
      // made it trail the cards on an all-in run-out (the hand races past 13, so
      // HoldEmCards passes skipFlip and the whole board snaps in at once while
      // these phase transitions arrive behind it).
      case '6':
      case '8':
      case '10':
        break;
      case '13':
        // FindWinners — showdown: hole cards flip face-up. Unity plays
        // PlayerFlipCards here (not a table-card sound). Parity fix.
        //
        // Unity emits this from the flipping player's own UIPlayer with
        // isOwnSound = IsPOVPlayer() (UIPlayer.cs:923), so with opponents muted
        // only the POV player's flip is audible. Gate on the acting seat to
        // match — this and the chips-to-pot below are hand-resolution cues that
        // previously bypassed the mute entirely, which is what leaked at the
        // final fold before a winner was determined.
        if (playsForActingSeat) playPlayerFlipCards();
        break;
      case '14':
        // NO cue. Hand 14 is Unity's PayWinners: it SWEEPS the board (see
        // HoldEmCards' shouldSweepBoard / ClearCardValue mirror), it does not
        // deal anything — and `handNum >= 13` already sets `skipFlip`, so no card
        // flips on screen either. This used to fire playTableCard, giving an
        // extra flip sound right after the river with no matching visual. Unity
        // plays TABLEFLIPCARDS only at DealFlop/DealTurn/DealRiver.
        break;
      case '15':
        // ShowCard — pot collection. Unity gates CHIPSTOPOT on IsPOVPlayer()
        // (UIPlayer.cs:524), and our own per-seat copy in Bet.tsx is gated the
        // same way; this global one was not.
        if (playsForActingSeat) playChipsToPot();
        setIsDealingPlayerCard(true);
        break;
    }

    // Deps deliberately unchanged from before this fix — see the ref comment
    // above. `muteOtherPlayers`/`seatId` are read through refs so toggling the
    // setting cannot re-trigger this effect's table-card cues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hand, lastplayer]);

  // Betting-round action cues (bet / check / fold).
  //
  // Driven by a snapshot of every seat's `p{n}action`, NOT by `lastplayer` /
  // `lastmove`. Those looked like per-action stamps but do not move on this
  // payload path — the Dynamo enrichment coerces a missing `lastplayer` to ""
  // (dynamoTableAdapter.js:278) — so an effect keyed on them never re-ran and the
  // de-dupe key never changed: the first cue played and every later one was
  // swallowed, whatever the mute setting was.
  //
  // Diffing the per-seat signature both triggers reliably AND tells us which seat
  // acted, so the mute gate can attribute the cue without `lastplayer`. Per-seat
  // fields are the pattern Bet.tsx already uses for its chip cues, and those work.
  const seatActions = seatActionsSignature(game);
  useEffect(() => {
    const changed = diffSeatAction(prevSeatActionsRef.current, seatActions);
    prevSeatActionsRef.current = seatActions;
    if (!changed) return;

    const actionSfx = resolveActionSfx({
      hand,
      lastplayer: changed.seatId,
      lastaction: changed.action,
      currentPlayerSeatId: seatIdRef.current,
      muteOtherPlayersSounds: muteOtherPlayersRef.current,
      muteOtherPlayersIsExplicit: muteIsExplicitRef.current
    });
    if (!actionSfx) return;

    // Own actions cross tables and are deduped, because crossTable means every
    // mounted copy of this table fires them (carousel panel AND grid cell) —
    // the same reason the turn cue claims. Opponents' stay table-local, where
    // only one panel is ever active, so they need no claim.
    const actorSeat = Number(changed.seatId);
    const ownSeat = Number(seatIdRef.current);
    const isOwnAction =
      Number.isFinite(actorSeat) && Number.isFinite(ownSeat) && actorSeat === ownSeat;

    // Which chip sample the ante rides — see resolveChipCue. Falls back to the
    // handful if the table has no readable ante or blind, i.e. what shipped
    // before this cue existed.
    const smallChips =
      resolveChipCue({ amount: anteRef.current, bigBlind: bigBlindRef.current }) === 'smallBet';

    if (isOwnAction) {
      // Dedupe passed to useSound rather than claimed here — a claim spent on a
      // gated play loses the cue outright (see WrapperOptions.dedupeKey).
      const key = `act:${actionSfx}:${actorSeat}`;
      if (actionSfx === 'bet') (smallChips ? playSmallBetOwn : playBetChipsOwn)({ dedupeKey: key });
      else if (actionSfx === 'check') playCheckOwn({ dedupeKey: key });
      else playFoldOwn({ dedupeKey: key });
      return;
    }
    if (actionSfx === 'bet') (smallChips ? playSmallBet : playBetChips)();
    else if (actionSfx === 'check') playCheck();
    else playFold();
    // Re-runs whenever any seat's action field changes; `hand` gates the phase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatActions, hand]);

  // Community-card cue — fires when the RENDERED board stage advances.
  //
  // `boardStageFromHand` mirrors HoldEmCards' `maxStage`, which is what actually
  // puts a card on screen. Driving this off the card COUNT was wrong: card1..card5
  // arrive in the payload before the hand advances (HoldEmCards notes a hand='6'
  // broadcast can still carry the PREVIOUS flop), so the cue fired ahead of the
  // render and the gap compounded street by street — visible on web, hidden on
  // native by different batching.
  //
  // `game.card1` is required for the same reason the render requires it, and the
  // first run only seeds the stage so joining a table mid-hand stays silent.
  const boardStage = boardStageFromHand(hand);
  const hasBoardCards = countBoardCards(game) > 0;
  useEffect(() => {
    const prev = prevBoardStageRef.current;
    prevBoardStageRef.current = boardStage;
    if (prev === null) return; // hydration / mid-hand join
    if (boardStage > prev && hasBoardCards) playTableCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardStage, hasBoardCards]);

  // Confetti fires in lockstep with each pot's chip-flight animation — see
  // the effect right after the pot-audio cue below, which shares the same
  // per-flight `potFlightKey` signal so the visual and the sound land on
  // the same frame for the same pot / Hi-Lo phase.
  //
  // Historical note: earlier passes triggered confetti on the winner
  // DECLARATION (hand=13 with a boolean "already fired" latch), which
  // burst once at winner announcement and stayed silent while side pots
  // subsequently flew — EN-10561 follow-up. Riding the chip-flight key
  // fires one burst per distribution, exactly when its chips move, and
  // only when THIS seat is on the receiving side.

  // Pot awarded — cue the chips actually flying from the pot to the seat.
  //
  // Triggered by `showdown.currentStep` reaching the step at which Pot.tsx mounts
  // WinChips, which useAnimatedWinStack documents as the same frame the chips
  // launch. It used to hang off the winners array at hand >= 13 (FIND_WINNERS),
  // i.e. the winner DECLARATION — an audibly earlier moment than the chip
  // animation, which is what was reported.
  //
  // Keyed per flight (pot index + Hi/Lo phase) so side pots and a scooped Hi-Lo
  // pot each get their own cue, matching the chips flying more than once.
  //
  // Attributed to the seat(s) being PAID, so "Mute Other Players' Sounds"
  // silences an opponent's pot while still sounding your own. Unity gates the
  // same payout cue per player (CHIPSTOPOT with isOwnSound: IsPOVPlayer(), in
  // UIPlayer.OnStackUpdated) — this was previously treated as table-level and
  // sounded through the mute, which was wrong on both counts.
  const potFlightKey = potChipFlightKey({
    currentStep: showdown?.currentStep,
    currentPot: showdown?.currentPot,
    handType: showdown?.handType
  });
  const prevPotFlightKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevPotFlightKeyRef.current;
    prevPotFlightKeyRef.current = potFlightKey;
    if (!potFlightKey || potFlightKey === prev) return;
    if (!isActiveTable) return; // held panel: consume, do not sound

    // Whoever this flight is paying. If that includes our seat the cue is our
    // own win; otherwise it belongs to an opponent and the mute decides.
    const winnerSeats = potFlightWinnerSeats(pots, showdown);
    const ownSeat = Number(seatIdRef.current);
    const actorSeatId = winnerSeats.includes(ownSeat) ? ownSeat : winnerSeats[0];

    const allowed = shouldPlayTableSfx({
      origin: 'player',
      actorSeatId,
      currentPlayerSeatId: seatIdRef.current,
      muteOtherPlayersSounds: muteOtherPlayersRef.current,
      muteOtherPlayersIsExplicit: muteIsExplicitRef.current
    });
    if (!allowed) return;

    // Winner rakes the pot — OUTCOMES tier. Was the sprite sheet's
    // `collectchips`; now an individual cue so it rides the same gates and
    // loudness ladder as everything else.
    playCollectChips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [potFlightKey, isActiveTable]);

  // Confetti fires on WINNER DECLARATION for each pot — the frame the WINNER
  // pill pops up on the winning seat, one step earlier than the chip flight
  // above (see potWinnerDeclarationKey vs potChipFlightKey). Same per-flight
  // identity as the audio's key, just activated at step 2 instead of 3, so
  // side pots and each Hi/Lo phase still get their own burst. The key stays
  // the same across step 2 → 3 → …, so the burst that fires at declaration
  // does not re-fire when chips subsequently launch.
  const declarationKey = potWinnerDeclarationKey({
    currentStep: showdown?.currentStep,
    currentPot: showdown?.currentPot,
    handType: showdown?.handType
  });
  // EN-11387: the streak moment waits for the winner of the hand, which on a
  // run it twice or three times is the LAST board's. Board 1's declaration
  // would start its next-hand wait while the later boards were still being
  // settled, so the count landed over their winners.
  const streakWinnerDeclaredKey =
    declarationKey && isFinalRunOutBoard(game) ? declarationKey : null;
  const prevConfettiKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevConfettiKeyRef.current;
    prevConfettiKeyRef.current = declarationKey;
    if (!declarationKey || declarationKey === prev) return;
    // VISIBLE, not focused — see canPaintTableCelebration. Every grid cell and
    // every tiled panel is a felt the player is looking at, so all of them may
    // celebrate; only the single-table carousel's held panels stay dark.
    if (!canPaintTableCelebration({ isActiveTable, gridMode })) return;

    // Only celebrate when this seat is on the receiving side of the pot
    // that was just declared, IN THIS Hi/Lo PHASE. Observers, folded
    // players, and side-pot losers stay quiet; in a Hi-Lo pot the Hi
    // winner is celebrated on the Hi half and the Lo winner on the Lo
    // half, rather than both being celebrated on both.
    const winnerSeats = potFlightWinnerSeats(pots, showdown);
    const ownSeat = typeof seatId === 'number' ? seatId : NaN;
    if (!isSeated || !Number.isFinite(ownSeat) || !winnerSeats.includes(ownSeat)) return;

    // Bump the key so React remounts <Confetti /> with a fresh particle
    // spawn even if the previous burst is still on screen — otherwise
    // setShowConfetti(true) while it's already true is a React no-op and
    // the next pot's confetti would be silently dropped.
    setConfettiKey((k) => k + 1);
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), CONFETTI_SHOW_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declarationKey, isActiveTable, gridMode]);

  // Scale removed — layout is now CSS grid based, no transform: scale()

  const pageRef = useRef<HTMLDivElement>(null);

  // Screen entrance animation — the felt fades up when you arrive at a table.
  // NOT when you switch between two open tables: that remounts this subtree
  // (the per-table providers are keyed by gameID), and replaying an 0.8s
  // fade-from-black on the incoming table is precisely what made a swipe look
  // like the page had reloaded. RebindingTableView is sliding the two tables
  // past each other at that moment, which is the only motion the switch needs.
  useLayoutEffect(() => {
    const el = pageRef.current;
    if (el && !isTableSwitching()) {
      gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.8, ease: 'power2.out' });
    }
    return () => {
      if (el) gsap.killTweensOf(el);
    };
  }, []);

  // Show the shared splash (via SplashScreenContext) while waiting for
  // currentTable to arrive — the full branded splash on the first-ever table
  // this session, the quicker FadeOverlay on any later one (in-session
  // navigation to an already-seen table). Skipped entirely in grid mode,
  // which shows neither (see the render below). _splashShown flips true as
  // soon as this component has shown the full splash once, either here or
  // via the self-dismissing intro further down.
  // The splash is app-level chrome, so only the table the player is on may raise
  // it — an inert panel waiting on its own state must not cover the felt.
  const tableNotReady = !currentTable && !gridMode && isActiveTable;
  useEffect(() => {
    if (!tableNotReady) return undefined;
    showSharedSplash('gameTableLoading', _splashShown ? 'light' : 'full');
    return () => hideSharedSplash('gameTableLoading');
  }, [tableNotReady, showSharedSplash, hideSharedSplash]);

  // DEFENSIVE ONLY — this guard cannot fire today, and no loading state may be
  // hung off it. `useTableSelector` returns an object on every path (the table's
  // own slice, state.currentTable, or the slice's initial state), and
  // state.currentTable is a configureStore slice, so `currentTable` is never
  // falsy. Proven in pages/__tests__/gameCurrentTableGuard.test.tsx.
  //
  // EN-10324's first pass put the grid's loading cell here, where a player could
  // never see it. The loading state a felt ACTUALLY shows while un-hydrated is
  // components/Table/TableLoadIndicator (rendered on `!tabletype`, sized for the
  // grid tile or the full felt); the terminal failure state is
  // TableLoadErrorModal. Both are mounted below. Note this also means the
  // `gameTableLoading` splash above never raises — tracked separately.
  if (!currentTable) {
    if (gridMode) return <div style={{ flex: 1, background: 'rgba(18, 16, 12, 0.95)' }} />;
    return null;
  }

  // The win celebration, built ONCE for both render trees below.
  //
  // GameInner returns two completely separate trees — the grid cell and the
  // single-table page — and the confetti was only ever placed in the
  // single-table one, so no amount of fixing the trigger could make it appear in
  // the multi-table grid: the component was never mounted there (reported —
  // "confetti animation is not working on Multitable grid view"). Sharing one
  // element between the two returns is what stops the next visual from going
  // missing in half the app.
  //
  // Both roots are `position: relative` (pageShell / gridCellStyle), which is
  // what the canvas anchors itself to — so it covers exactly this table, felt
  // and footer included, in whichever view it lands.
  const celebration = showConfetti ? (
    <Confetti key={confettiKey} duration={CONFETTI_FALL_SECS} />
  ) : null;
  // EN-11163: the streak moment, owed once per day and played on the felts on
  // screen (ops.streaks). The single-table view supplies the shared header;
  // grid cells don't.
  const streakMoment = (
    <StreakTableMoment
      gameType={gameType}
      gameID={gameID}
      gameNo={streakGameNo}
      winnerDeclaredKey={streakWinnerDeclaredKey}
      dealerSeat={streakDealerSeat}
      surface={suppliesSharedHeader ? 'table' : 'grid'}
      visible={onScreen && activeView === (suppliesSharedHeader ? 'table' : 'grid')}
      focused={isActiveTable}
      free={streakFree}
      seated={isSeated}
    />
  );

  // Grid mode: compact table view with modals
  if (gridMode) {
    return (
      // data-table-view-root: the non-transformed container WinChips portals
      // into so shipping chips stack below this cell's modals. See WinChips.tsx.
      <div style={gridCellStyle} data-table-view-root>
        {celebration}
        {/* Shared nav for whichever tile is focused, portalled out of the
            moving track so it renders once. No Sit Out / Exit cluster of its
            own here — each tile carries its own below. */}
        {suppliesSharedHeader && (
          <FixedChrome>
            {/* panelView pin: keeps this painted through the table↔grid cut. */}
            <TableHeader
              compact
              tableOverlay
              panelView='table'
              onTickerVisibleChange={setTickerRailVisible}
            />
          </FixedChrome>
        )}
        <div style={gridFeltStyle}>
          {/* EN-10520: Table No / Hand No lives on the felt, not this corner. */}
          <Table />
          {/* "I'm Back" prompt while sitting out, scoped to this cell. */}
          <SitOutOverlay />
          {/* EN-11109: Go to lobby chip / rematch line on a finished Sit & Go. */}
          {sitGoResultViews && <SitGoFinishedOverlay />}
          <GamePromoProgressIndicator cashTablesOnly />
          {streakMoment}
          {/* Table controls (Sit Out / Observe / Exit Table), scoped to this
              cell since the grid has no per-cell TableHeader. Shifts down with
              the shared header's ticker so it doesn't cover Exit Table — only
              for the cell that actually supplies (and can overlap) that ticker. */}
          <div
            style={applyTickerShift(gridTableActionRail, suppliesSharedHeader && tickerRailVisible)}
          >
            <TableActions />
          </div>
        </div>
        {(tableType === 'm' || tableType === 't') && tStatus !== 'C' && (
          <TournamentDetails
            tableType={tableType}
            gameTitle={title}
            tournamentId={tournamentId}
            tournamentPrizePool={prizePool}
            tournamentStatus={tStatus}
          />
        )}
        <div style={gridFooterStyle}>
          <UserActions />
        </div>
        {/* Seat-offer modal retired: a ready seat now shows as a JOIN countdown
            chip in the table nav row (and the lobby "Your Tables" strip) rather
            than a disruptive overlay. Claiming still flags acceptedOfferGameId,
            which the buy-in effect below consumes. */}
        <BuyInModal />
        {/* Seat rejection for THIS cell only — GameModal portals it into the
            cell root, so the backdrop no longer blurs every open table. */}
        <SeatJoinErrorModal />
        {game.privateTable ? <PrivateTablePasswordModal /> : ''}
        {/* DuplicateScreenModal is mounted once at the App level — see App.tsx.
            Mounting it here would shadow the app-level isDuplicateAccess flag
            with this cell's local <GlobalProvider> (from MultiTableGrid) and
            silently hide the modal in grid view. */}
        {(tableType === 'm' || tableType === 't') && <TournamentScreenModal />}
        {sitGoResultViews && <SitGoResultModal />}
        {sitGoResultViews && <SitGoRematchRegistration />}
        <WaitForBuyInModal />
        <AddChipsModal />
        <AddOnChipsModal />
        <RebuyChipsModal />
        <AddFundsModal />
        {/* ErrorMsgModal is mounted once at the app level (see App.tsx) and
            driven by the redux errorMsg.open flag, so it isn't rendered per
            grid cell here (that duplicated/scoped it incorrectly). */}
        <AutoFoldMessageModal />
        {/* Unity-parity terminal state for a table that never loads — see
            TableLoadErrorModal / useTableLoadTimeout. */}
        <TableLoadErrorModal />
        {!guest && <HandHistoryPanel />}
        {!guest && <Chat />}
      </div>
    );
  }

  return (
    // data-table-view-root: non-transformed container WinChips portals into so
    // shipping chips stack below the in-table modals. See WinChips.tsx.
    <div ref={pageRef} style={pageShell} data-table-view-root>
      {celebration}
      {/* Shared header — hamburger / Add Chips / support, the multi-table
          navigation strip, and the Sit Out / Exit Table controls.
          tableOverlay → landscape renders as floating overlays on the felt.
          Wrapped in FixedChrome so it does NOT ride the carousel: these are
          global controls, and sliding them makes the whole app look like it is
          moving rather than the table. It stays in this subtree (so it keeps
          reading THIS table's state and GameContext) — only its DOM is
          portalled out of the moving track. */}
      <FixedChrome>
        {/* panelView pin: the table panel stays painted through the table↔grid
            cut after `activeView` flips — see TableHeaderProps. */}
        <TableHeader
          compact
          tableOverlay
          panelView='table'
          onTickerVisibleChange={setTickerRailVisible}
        />
      </FixedChrome>
      {/* Row 1: Table area — fills the viewport */}
      <div style={tableRow} ref={elementRef}>
        {/* Table No / Hand No now renders on the felt between the board and the
            blinds line — see <FeltMeta /> inside <Table />. */}
        {/* SoftLoadingState removed — splash screen handles initial load */}
        <Table />
        {/* Big centered "I'm Back" prompt while sitting out */}
        <SitOutOverlay />
        {/* EN-11109: Go to lobby chip / rematch line on a finished Sit & Go. */}
        {sitGoResultViews && <SitGoFinishedOverlay />}
        {!guest && <GamePromoProgressIndicator cashTablesOnly />}
        {!guest && streakMoment}
        <Toaster />
        {/* Engine-driven table-control rail, floating under the header balance
            pill in landscape. Portrait orientation override for the stacked
            Saloon-pill button style. */}
        {showTableActionRail && (
          <FixedChrome>
            <div style={applyTickerShift(tableActionRail, tickerRailVisible)}>
              <OrientationOverrideProvider orientation='portrait'>
                <TableActions />
              </OrientationOverrideProvider>
            </div>
          </FixedChrome>
        )}
      </div>

      {/* Tournament details overlay — outside tableRow so position:fixed works correctly */}
      {(tableType === 'm' || tableType === 't') && tStatus !== 'C' && (
        <TournamentDetails
          tableType={tableType}
          gameTitle={title}
          tournamentId={tournamentId}
          tournamentPrizePool={prizePool}
          tournamentStatus={tStatus}
        />
      )}

      {/* Row 2: Bottom taskbar */}
      <div style={footerRow}>
        <UserActions />
      </div>

      {/* Modals and overlays — only render when table is active */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          // Above the floating header chrome (see fixedChromeHostStyle): an
          // open modal, Table Chat or Table History must cover it, not sit
          // under the hamburger and "+".
          zIndex: activeView === 'table' ? TABLE_OVERLAY_Z : -1,
          overflow: 'hidden',
          visibility: activeView === 'table' ? 'visible' : 'hidden'
        }}
      >
        <div style={{ pointerEvents: activeView === 'table' ? 'auto' : 'none' }}>
          <BuyInModal />
          {/* Seat rejection for THIS table — see SeatJoinErrorModal. */}
          <SeatJoinErrorModal />
          {game.privateTable ? <PrivateTablePasswordModal /> : ''}
          {/* DuplicateScreenModal is mounted once at the App level — see App.tsx. */}
          {(tableType === 'm' || tableType === 't') && <TournamentScreenModal />}
          {sitGoResultViews && <SitGoResultModal />}
          {sitGoResultViews && <SitGoRematchRegistration />}
          <WaitForBuyInModal />
          <AddChipsModal />
          <AddOnChipsModal />
          <RebuyChipsModal />
          <AddFundsModal />
          {/* ErrorMsgModal is mounted once at the app level (see App.tsx). */}
          <AutoFoldMessageModal />
          {/* Unity-parity terminal state for a table that never loads — see
              TableLoadErrorModal / useTableLoadTimeout. */}
          <TableLoadErrorModal />
          <FreshDeskLink />
          {!guest && <HandHistoryPanel />}
          {!guest && <Chat />}
        </div>
      </div>
    </div>
  );
};

export { GameInner };
export default Game;

/**
 * The winner's confetti, sized to end with the WINNER badge (~3.5 s) rather than
 * outlast it: Confetti falls for `duration`, then fades over its last second.
 * It used to fall 4 s and stay up 5 s, past the trimmed pace's felt clear.
 * EN-10985.
 */
const CONFETTI_FALL_SECS = 2.5;
const CONFETTI_SHOW_MS = 3500;

const _persistentLogoStyle: CSSProperties = {
  // Replaced by TableHeader compact mode
  position: 'fixed',
  top: 12,
  left: 16,
  width: '80px',
  zIndex: 50,
  opacity: 0.8,
  pointerEvents: 'none'
};

// pageShell (the outermost flex column, incl. the safe-area-top padding) moved
// to gamePageLayout.ts so the EN-10420 stories mount the identical shell.
// (Merge note 8/13: main's local `pageStyle` was property-identical to
// gamePageLayout's pageShell and had no remaining references, so it was
// dropped in favour of the relocation; the carousel/chrome constants below
// arrived with the multi-table navigation strip and stay.)

// The stage holds the sliding carousel AND the stationary top chrome above it.
const tableStageStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%'
};

// Where the top chrome lands: over the panels, full-bleed, and pass-through so
// only the header's own buttons take pointer events (the felt behind its empty
// areas stays draggable for the swipe gesture).
//
// The z-index has to land in a specific gap. ABOVE the felt, its footer and the
// action rail (all ≤ 40) so the header floats over the table as it always has —
// but BELOW the table's own overlay layer at TABLE_OVERLAY_Z (50), which holds
// the modals, Table Chat and Table History. Those used to win simply by coming
// later in the DOM than the header; hoisting the header out of the panel and
// giving it a higher z put it in front of them, so an open Chat or History
// panel had the hamburger, support and "+" floating on top of it.
export const TABLE_OVERLAY_Z = 50;
export const fixedChromeHostStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
  zIndex: TABLE_OVERLAY_Z - 5
};

// Grid-cell layout (felt / fixed footer) lives in gameGridLayout.ts so the
// "footer never steals felt height" invariant is unit-testable in isolation.

// Per-cell table-control cluster (Sit Out / Observe / Exit Table), anchored
// top-right of the grid cell's felt.
const gridTableActionRail: CSSProperties = {
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 40
};

// Right rail for the table-control cluster — sits just below the floating
// header balance pill (top ~80px), per the Saloon landscape design.
// Exported so a test can pin the pointerEvents below: it is portalled into the
// pass-through fixed-chrome host, where losing that line silently kills the
// landscape Add Chips / Sit Out / Exit Table buttons.
export const tableActionRail: CSSProperties = {
  position: 'absolute',
  // Top-align with the header's left cluster (hamburger / support), which sits
  // at safe-area-top + 8px. Was 80 to clear the old balance pill, which has
  // since moved into the hamburger menu.
  top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
  right: 10,
  width: 84,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '0.3rem',
  zIndex: 40,
  // The rail is portalled into the fixed-chrome host, which is pass-through so
  // the felt behind it stays draggable for the swipe. Pointer events therefore
  // have to be re-enabled HERE — inheriting them from an ancestor (which is how
  // this cluster used to get them) is no longer possible, and without this the
  // Add Chips / Sit Out / Exit Table buttons are dead in landscape.
  pointerEvents: 'auto'
};
